import React, { createContext, useContext, useState, useEffect } from "react";
import { getTaggingFilters } from "../api/dashboard";

interface FilterContextType {
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedBrands: string[];
  setSelectedBrands: (brands: string[]) => void;
  selectedDateRange: [Date | null, Date | null];
  setSelectedDateRange: (dateRange: [Date | null, Date | null]) => void;
  filters: {
    source_category_id?: Array<{
      category_id: string;
      category_name: string;
      total_count: number;
    }>;
    source_brand?: Array<{
      brand_id: string;
      brand_name: string;
      total_count: number;
    }>;
    date_created?: {
      data: {
        min: string;
        max: string;
      };
      key: string;
    };
  };
  loading: boolean;
  setLoading: (loading: boolean) => void;
  refreshFilters: (
    newCategories?: string[], 
    newBrands?: string[], 
    newDateRange?: [Date | null, Date | null]
  ) => Promise<void>;
  resetFilters: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
};

export const FilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [filters, setFilters] = useState<any>({});
  const [initialFilters, setInitialFilters] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const refreshFilters = async (
    newCategories?: string[], 
    newBrands?: string[],
    newDateRange?: [Date | null, Date | null]
  ) => {
    try {
      const response = await getTaggingFilters({
        selectedCategories: newCategories || selectedCategories,
        selectedBrands: newBrands || selectedBrands,
        selectedDateRange: newDateRange || selectedDateRange,
      });
      setFilters(response.data);
    } catch (error) {
      console.error("Error refreshing filters:", error);
      throw error;
    }
  };

  useEffect(() => {
    setInitialFilters(filters);
  }, [filters]);

  const resetFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedDateRange([null, null]);
  };

  useEffect(() => {
    const fetchInitialFilters = async () => {
      try {
        const response = await getTaggingFilters();
        setFilters(response.data);
      } catch (error) {
        console.error("Error fetching initial filters:", error);
      }
    };
    fetchInitialFilters();
  }, []);

  return (
    <FilterContext.Provider
      value={{
        selectedCategories,
        setSelectedCategories,
        selectedBrands,
        setSelectedBrands,
        selectedDateRange,
        setSelectedDateRange,
        filters,
        loading,
        setLoading,
        refreshFilters,
        resetFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};